import { AnalyticsBrowser } from '@segment/analytics-next';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { router } from './router';
import { useDappState } from '@src/hooks/useDappState';

const AnalyticsContext = createContext<AnalyticsBrowser>(undefined!);

type Props = {
  writeKey: string;
  children: React.ReactNode;
};

export const useAnalyticsDefaultValues = () => {
  const { account, partBalance } = useDappState();

  return {
    address: account?.address,
    network: account?.network,
    partBalance: partBalance,
  };
};

export const useAnalytics = () => {
  const result = useContext(AnalyticsContext);
  if (!result) {
    throw new Error('Context used outside of its Provider!');
  }
  return result;
};

export enum AnalyticsEvents {
  ReferralCodeShareClicked = 'Referral Code Share Clicked',
}

export const AnalyticsProvider = ({ children, writeKey }: Props) => {
  const analytics = useMemo(
    () => AnalyticsBrowser.load({ writeKey }),
    [writeKey]
  );

  const { address, network, partBalance } = useAnalyticsDefaultValues();

  useEffect(() => {
    if (!analytics) return;

    const unsubscribe = router.subscribe('onLoad', (event) => {
      if (!event.hrefChanged) return;
      const matches = router.state.matches;
      const match = matches[matches.length - 1];
      /* @ts-ignore-next-line */
      const title = match.staticData.title ?? '';

      void analytics.page(title, {
        path: event.toLocation.pathname,
        search: event.toLocation.searchStr,
        address: address,
        network: network,
        partBalance: partBalance,
      });
    });

    return () => {
      unsubscribe();
    };
  }, [analytics, address, network, partBalance]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};
